import React, { useEffect, useMemo, useState } from 'react'
import { Modal, Button } from 'react-bootstrap'
import Select from 'react-select'
import { STRIPE_PRIMARY } from 'constants/stripe'

interface CouponManageModalProps {
  show: boolean
  onClose: () => void
  onSave: (nextCoupons: any[]) => void
  coupons: any[]
  availableCoupons: any[]
}

const CouponManageModal: React.FC<CouponManageModalProps> = ({
  show,
  onClose,
  onSave,
  coupons,
  availableCoupons,
}) => {
  const options = useMemo(
    () =>
      (availableCoupons || []).map((c: any) => {
        const label = c.name || c.id

        let description = ''

        if (c.percent_off) {
          description += `${c.percent_off}% off`
        } else if (c.amount_off) {
          description += `$${(c.amount_off / 100).toFixed(2)} off`
        }

        if (c.duration) {
          if (c.duration === 'forever') {
            description += ' - forever'
          } else if (c.duration === 'once') {
            description += ' - one time use'
          } else if (c.duration === 'repeating' && c.duration_in_months) {
            description += ` - for ${c.duration_in_months} month${
              c.duration_in_months > 1 ? 's' : ''
            }`
          } else {
            description += ` - ${c.duration}`
          }
        }

        if (c.valid === false) {
          description += ' - expired'
        }

        if (c.metadata?.description) {
          description += ` - ${c.metadata.description}`
        }

        return {
          value: c.id,
          label,
          description,
          metadata: {
            percent_off: c.percent_off,
            amount_off: c.amount_off,
            duration: c.duration,
            duration_in_months: c.duration_in_months,
            valid: c.valid,
            metadata: c.metadata,
          },
        }
      }),
    [availableCoupons]
  )

  // rows hold coupon ids (string)
  const [rows, setRows] = useState<string[]>([])

  useEffect(() => {
    setRows(
      (coupons || []).map((c) => c?.couponDetails?.id || c?.id).filter(Boolean)
    )
  }, [coupons])

  const setRow = (idx: number, value: string) => {
    setRows((prev) => prev.map((v, i) => (i === idx ? value : v)))
  }

  const addRow = () => setRows((prev) => [...prev, ''])
  const removeRow = (idx: number) =>
    setRows((prev) => prev.filter((_, i) => i !== idx))

  const handleSubmit = () => {
    // normalize to coupon objects used by CouponManager
    const ids = rows.filter(Boolean)
    // de-dup while preserving order
    const seen = new Set<string>()
    const uniqueIds = ids.filter((id) =>
      seen.has(id) ? false : (seen.add(id), true)
    )

    const next = uniqueIds
      .map((id) => availableCoupons.find((c: any) => c.id === id))
      .filter(Boolean)
      .map((coupon: any) => ({
        id: coupon.id,
        name: coupon.name || coupon.id,
        status: 'active',
        couponDetails: coupon,
        percent_off: coupon.percent_off,
        amount_off: coupon.amount_off,
        duration: coupon.duration,
        duration_in_months: coupon.duration_in_months,
        valid: coupon.valid,
        metadata: coupon.metadata,
      }))

    onSave(next)
  }

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header
        closeButton
        style={{
          backgroundColor: '#ffffff',
          borderBottom: '1px solid #e6ebf1',
        }}
      >
        <Modal.Title
          style={{ fontSize: 18, fontWeight: 600, color: '#000000' }}
        >
          Update subscription coupons
        </Modal.Title>
      </Modal.Header>
      <Modal.Body style={{ backgroundColor: '#ffffff' }}>
        <div style={{ color: '#6b7c93', marginBottom: 12 }}>
          Coupons can be stacked—the order affects the final price.
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {rows.map((value, idx) => (
            <div
              key={idx}
              style={{ display: 'flex', alignItems: 'center', gap: 8 }}
            >
              <div style={{ flex: 1 }}>
                <Select
                  options={options}
                  value={options.find((o: any) => o.value === value) || null}
                  onChange={(opt: any) => setRow(idx, opt?.value || '')}
                  placeholder="Choose a coupon..."
                  isClearable
                  formatOptionLabel={(option: any, { context }: any) => {
                    if (context === 'menu') {
                      return (
                        <div style={{ padding: '4px 0' }}>
                          <div
                            style={{
                              fontWeight: '500',
                              fontSize: '13px',
                              color: 'inherit',
                            }}
                          >
                            {option.label}
                          </div>
                          {option.description && (
                            <div
                              style={{
                                fontSize: '11px',
                                color: 'inherit',
                                opacity: 0.8,
                                marginTop: '2px',
                                lineHeight: '1.3',
                              }}
                            >
                              {option.description}
                            </div>
                          )}
                        </div>
                      )
                    }
                    return option.label
                  }}
                  styles={{
                    control: (base: any) => ({
                      ...base,
                      backgroundColor: '#ffffff',
                      borderColor: '#e6ebf1',
                      minHeight: '38px',
                    }),
                    singleValue: (base: any) => ({
                      ...base,
                      color: '#000000',
                      fontSize: '13px',
                      lineHeight: '1.4',
                    }),
                    input: (base: any) => ({
                      ...base,
                      color: '#000000',
                      fontSize: '13px',
                    }),
                    placeholder: (base: any) => ({
                      ...base,
                      color: '#000000',
                      fontSize: '13px',
                    }),
                    menu: (base: any) => ({
                      ...base,
                      backgroundColor: '#ffffff',
                      border: '1px solid #e6ebf1',
                      maxHeight: '300px',
                    }),
                    option: (base: any, state: any) => ({
                      ...base,
                      color: state.isSelected ? '#ffffff' : '#000000',
                      backgroundColor: state.isSelected
                        ? '#635bff'
                        : state.isFocused
                        ? '#f8f9fa'
                        : '#ffffff',
                      padding: '8px 12px',
                      fontSize: '13px',
                      lineHeight: '1.4',
                      whiteSpace: 'normal',
                      wordWrap: 'break-word',
                      '&:hover': {
                        backgroundColor: state.isSelected
                          ? '#635bff'
                          : '#f0f2f5',
                      },
                    }),
                  }}
                />
              </div>
              <button
                type="button"
                onClick={() => removeRow(idx)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: '#e53d3d',
                  cursor: 'pointer',
                  fontSize: 20,
                  lineHeight: 1,
                }}
                aria-label="Remove coupon"
                title="Remove coupon"
              >
                ×
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addRow}
            style={{
              background: 'none',
              border: 'none',
              color: STRIPE_PRIMARY,
              cursor: 'pointer',
              fontSize: 14,
              fontWeight: 500,
              padding: 0,
              textDecoration: 'none',
              alignSelf: 'flex-start',
            }}
          >
            Add another coupon
          </button>
        </div>
      </Modal.Body>
      <Modal.Footer
        style={{ backgroundColor: '#ffffff', borderTop: '1px solid #e6ebf1' }}
      >
        <Button variant="outline-secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          style={{
            backgroundColor: STRIPE_PRIMARY,
            borderColor: STRIPE_PRIMARY,
          }}
          onClick={handleSubmit}
        >
          Submit
        </Button>
      </Modal.Footer>
    </Modal>
  )
}

export default CouponManageModal
